/**
 * DOCX — reading, and text editing that does not disturb the rest of the file
 * (§8 Phase 2, §16).
 *
 * The reading model is a block list: paragraphs with their runs, and tables of
 * cells of paragraphs. It is deliberately not a full layout model. §16 asks for
 * text, headings, lists, tables, images and basic character formatting, and
 * being explicit about the ceiling is what lets the loss list be honest.
 *
 * Saving never serialises that model. An edit is a span replacement inside
 * `word/document.xml`, and every other part is copied in its original
 * compressed form. A document with tracked changes, comments, embedded fonts,
 * a VBA project and SmartArt therefore comes back with all of them intact,
 * because the save path never looked at any of it (§13, §24).
 *
 * What this handler will not do: fetch anything a document points at. External
 * relationship targets are parsed so a link can be shown and copied, never
 * resolved (§29, §30).
 */
import { FORMATS_BY_ID, formatFileSize } from '@moduviewer/core';
import type {
  ExportResult, FileSource, FormatHandler, HandlerContext,
  HandlerWarning, OpenedDocument, Preview, ValidationReport,
} from '../contract.js';
import { HandlerError, throwIfAborted } from '../contract.js';
import {
  hasPart, openPackage, partsWithPrefix, readPart, readPartText, repackPackage,
} from '../ooxml/package.js';
import type { OpcPackage } from '../ooxml/package.js';
import {
  applyParagraphEdits, parseBody, parseOrderedNumbering,
  parseRelationships, parseStyleNames,
} from './document-xml.js';
import type {
  DocxBlock, DocxParagraph, DocxTable, ParseOptions,
} from './document-xml.js';

export type { DocxBlock, DocxParagraph, DocxTable, DocxRun } from './document-xml.js';

const DOCUMENT_PART = 'word/document.xml';
const PREVIEW_BLOCKS = 40;
/** EMU per CSS pixel at 96 dpi — the unit Word stores picture sizes in. */
const EMU_PER_PX = 9525;

export interface DocxImage {
  readonly part: string;
  readonly bytes: Uint8Array;
  readonly contentType: string;
}

export interface DocxModel {
  readonly blocks: readonly DocxBlock[];
  readonly paragraphs: readonly DocxParagraph[];
  /** Pending text edits, paragraph index → new text. Applied at export (§13). */
  readonly edits: ReadonlyMap<number, string>;
  /** Decoded picture parts, keyed by part name, for rendering. */
  readonly images: ReadonlyMap<string, DocxImage>;
  readonly hasMacros: boolean;
  readonly hasTrackedChanges: boolean;
  readonly hasComments: boolean;
  readonly hasHeadersOrFooters: boolean;
  readonly hasFootnotes: boolean;
  readonly wordCount: number;
}

/* ------------------------------------------------------------------ *
 * package inspection
 * ------------------------------------------------------------------ */

function readOptionalText(pkg: OpcPackage, name: string): string {
  return hasPart(pkg, name) ? readPartText(pkg, name) : '';
}

function parseOptionsFor(pkg: OpcPackage, maxBlocks: number): ParseOptions {
  return {
    styleNames: parseStyleNames(readOptionalText(pkg, 'word/styles.xml')),
    orderedNumbering: parseOrderedNumbering(readOptionalText(pkg, 'word/numbering.xml')),
    rels: parseRelationships(readOptionalText(pkg, 'word/_rels/document.xml.rels')),
    maxBlocks,
  };
}

const IMAGE_TYPES: Readonly<Record<string, string>> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
  bmp: 'image/bmp', webp: 'image/webp', tiff: 'image/tiff', tif: 'image/tiff',
  emf: 'image/emf', wmf: 'image/wmf', svg: 'image/svg+xml',
};

/**
 * Decode only the pictures the body actually references.
 *
 * A document can carry media it no longer displays — an old logo, a cropped
 * original. Decoding those would spend the pixel budget on something nobody
 * asked to see (§33), so the reference list decides.
 */
function readImages(
  pkg: OpcPackage, blocks: readonly DocxBlock[], budgetBytes: number,
): { images: Map<string, DocxImage>; skipped: string[] } {
  const wanted = new Set<string>();
  const collect = (block: DocxBlock): void => {
    if (block.kind === 'paragraph') {
      for (const run of block.runs) if (run.kind === 'image' && run.part) wanted.add(run.part);
      return;
    }
    for (const row of block.rows) for (const cell of row) for (const p of cell.paragraphs) collect(p);
  };
  for (const block of blocks) collect(block);

  const images = new Map<string, DocxImage>();
  const skipped: string[] = [];
  let spent = 0;

  for (const part of wanted) {
    if (!hasPart(pkg, part)) { skipped.push(part); continue; }
    const extension = part.slice(part.lastIndexOf('.') + 1).toLowerCase();
    const contentType = IMAGE_TYPES[extension];
    if (!contentType) { skipped.push(part); continue; }
    try {
      const bytes = readPart(pkg, part);
      if (spent + bytes.length > budgetBytes) { skipped.push(part); continue; }
      spent += bytes.length;
      images.set(part, { part, bytes, contentType });
    } catch {
      skipped.push(part);
    }
  }
  return { images, skipped };
}

function countWords(paragraphs: readonly DocxParagraph[]): number {
  let total = 0;
  for (const paragraph of paragraphs) {
    const trimmed = paragraph.text.trim();
    if (trimmed) total += trimmed.split(/\s+/).length;
  }
  return total;
}

/* ------------------------------------------------------------------ *
 * handler
 * ------------------------------------------------------------------ */

export const docxHandler: FormatHandler<DocxModel> = {
  id: 'docx',
  formatIds: ['docx'],

  async probe(source: FileSource, ctx: HandlerContext): Promise<Preview> {
    throwIfAborted(ctx.signal);
    const pkg = openPackage(source.bytes);
    if (!hasPart(pkg, DOCUMENT_PART)) {
      throw new HandlerError('error.damagedOrIncomplete', `missing ${DOCUMENT_PART}`);
    }

    // §11: enough for a first screen, and no more. Styles and numbering are
    // read because a heading that renders as body text is worse than waiting.
    const xml = readPartText(pkg, DOCUMENT_PART);
    const body = parseBody(xml, parseOptionsFor(pkg, PREVIEW_BLOCKS));

    return {
      kind: 'pages',
      // A word processor has no page count until it has been laid out, and
      // inventing one would be a §19 claim we cannot back. Paragraphs are what
      // the file actually contains.
      unitCount: body.truncated ? undefined : body.paragraphs.length,
      unitKey: 'viewer.paragraph',
      firstContent: {
        blocks: body.blocks.slice(0, PREVIEW_BLOCKS),
        more: body.truncated,
      },
      warnings: [],
    };
  },

  async open(source: FileSource, ctx: HandlerContext): Promise<OpenedDocument<DocxModel>> {
    throwIfAborted(ctx.signal);
    const pkg = openPackage(source.bytes);
    if (!hasPart(pkg, DOCUMENT_PART)) {
      throw new HandlerError('error.damagedOrIncomplete', `missing ${DOCUMENT_PART}`);
    }

    const xml = readPartText(pkg, DOCUMENT_PART);
    throwIfAborted(ctx.signal);

    // One block per element; the ceiling exists so a generated file with a
    // million empty paragraphs fails a check instead of the tab.
    const maxBlocks = Math.max(1, Math.floor(ctx.limits.maxSpreadsheetCells / 8));
    const body = parseBody(xml, parseOptionsFor(pkg, maxBlocks));
    if (body.truncated) {
      throw new HandlerError(
        'error.unableToRead',
        `document exceeds the block budget of ${maxBlocks}`,
      );
    }
    throwIfAborted(ctx.signal);

    const { images, skipped } = readImages(pkg, body.blocks, ctx.limits.maxFileBytes / 4);

    const hasMacros = pkg.directory.entries.some((e) => e.name.endsWith('vbaProject.bin'));
    const hasTrackedChanges = /<w:ins\b|<w:del\b|<w:rPrChange\b|<w:pPrChange\b/.test(xml);
    const hasComments = hasPart(pkg, 'word/comments.xml');
    const hasFootnotes = hasPart(pkg, 'word/footnotes.xml') && /<w:footnoteReference/.test(xml);
    const headerFooterParts = partsWithPrefix(pkg, 'word/header')
      .concat(partsWithPrefix(pkg, 'word/footer'));

    const spec = FORMATS_BY_ID.get(source.formatId);
    const warnings: HandlerWarning[] = [];

    if (hasMacros) {
      warnings.push({
        messageKey: 'detect.warning.macrosBlocked',
        severity: 'caution',
        detail: 'vbaProject.bin is preserved byte-for-byte on save and never executed (§16, §30).',
      });
    }
    if (hasTrackedChanges) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'info',
        detail: 'Tracked changes are shown as marked text. Accepting or rejecting them is not supported, and paragraphs containing them are not editable here (§16).',
      });
    }
    if (hasComments) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'info',
        detail: 'This document has comments. They are preserved on save but not displayed in this release (§16).',
      });
    }
    if (headerFooterParts.length > 0) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'info',
        detail: `${headerFooterParts.length} header/footer parts are preserved on save but not displayed.`,
      });
    }
    if (skipped.length > 0) {
      warnings.push({
        messageKey: 'error.featuresUnsupported',
        severity: 'degraded',
        detail: `${skipped.length} pictures could not be decoded for display; they are preserved on save.`,
      });
    }
    if (source.sizeBytes > 20 * 1024 * 1024) {
      warnings.push({
        messageKey: 'status.loadingRemaining',
        severity: 'info',
        detail: `large document (${formatFileSize('en', source.sizeBytes)})`,
      });
    }

    return {
      formatId: source.formatId,
      model: {
        blocks: body.blocks,
        paragraphs: body.paragraphs,
        edits: new Map(),
        images,
        hasMacros,
        hasTrackedChanges,
        hasComments,
        hasHeadersOrFooters: headerFooterParts.length > 0,
        hasFootnotes,
        wordCount: countWords(body.paragraphs),
      },
      readOnly: ctx.assessment.forceReadOnly,
      editLevel: spec?.edit ?? 'basic-edit',
      lossOnSave: spec?.lossOnSave ?? [],
      warnings,
    };
  },

  async exportBytes(
    model: DocxModel, source: FileSource, ctx: HandlerContext,
  ): Promise<ExportResult> {
    throwIfAborted(ctx.signal);
    const pkg = openPackage(source.bytes);
    const replacements = new Map<string, Uint8Array>();
    const dropped: string[] = [];

    if (model.edits.size > 0) {
      // Re-parse from the original bytes so the spans being replaced are the
      // ones this XML actually has, not ones remembered from another read.
      const xml = readPartText(pkg, DOCUMENT_PART);
      const body = parseBody(xml, parseOptionsFor(pkg, Number.MAX_SAFE_INTEGER));
      const result = applyParagraphEdits(
        xml,
        body.paragraphs,
        [...model.edits].map(([index, text]) => ({ index, text })),
      );
      if (result.failed.length > 0) {
        throw new HandlerError(
          'error.unableToRead',
          `could not rewrite paragraphs: ${result.failed.join(', ')}`,
        );
      }
      dropped.push(...result.dropped);
      replacements.set(DOCUMENT_PART, new TextEncoder().encode(result.xml));
    }

    const repacked = repackPackage(pkg, replacements);

    return {
      bytes: repacked.bytes,
      suggestedName: source.filename,
      formatId: source.formatId,
      dropped,
    };
  },

  async validateExport(
    bytes: Uint8Array, model: DocxModel, ctx: HandlerContext,
  ): Promise<ValidationReport> {
    throwIfAborted(ctx.signal);
    const issues: string[] = [];
    let reopened = false;
    let contentPreserved = false;

    try {
      const pkg = openPackage(bytes);
      const xml = readPartText(pkg, DOCUMENT_PART);
      const body = parseBody(xml, parseOptionsFor(pkg, Number.MAX_SAFE_INTEGER));
      reopened = true;

      if (body.paragraphs.length !== model.paragraphs.length) {
        issues.push(`paragraph count changed: ${model.paragraphs.length} → ${body.paragraphs.length}`);
      }

      // Everything that was not edited must read back identically. This is the
      // check that would catch a repack quietly dropping content.
      for (const [i, expected] of model.paragraphs.entries()) {
        const actual = body.paragraphs[i];
        if (!actual) { issues.push(`paragraph ${i} disappeared`); continue; }
        const edited = model.edits.get(expected.index);
        const want = edited ?? expected.originalText;
        if (actual.text !== want) {
          issues.push(`paragraph ${i} text changed: ${JSON.stringify(want)} → ${JSON.stringify(actual.text)}`);
        }
        if (edited === undefined && actual.styleId !== expected.styleId) {
          issues.push(`paragraph ${i} lost its style (${expected.styleId})`);
        }
      }

      if (model.hasMacros && !pkg.directory.entries.some((e) => e.name.endsWith('vbaProject.bin'))) {
        issues.push('macro storage was lost during save');
      }
      if (model.hasComments && !hasPart(pkg, 'word/comments.xml')) {
        issues.push('comments part was lost during save');
      }
      for (const part of model.images.keys()) {
        if (!hasPart(pkg, part)) issues.push(`picture part missing: ${part}`);
      }

      contentPreserved = issues.length === 0;
    } catch (err) {
      issues.push(`could not reopen the exported document: ${(err as Error).message}`);
    }

    return { ok: reopened && contentPreserved, reopened, contentPreserved, issues };
  },
};

/* ------------------------------------------------------------------ *
 * editing helpers
 * ------------------------------------------------------------------ */

/**
 * Queue a paragraph's new text. Nothing is written until export (§13).
 *
 * Refuses paragraphs the save path cannot rebuild — those holding a picture, a
 * field, a footnote anchor or a tracked change. Refusing is the point: the
 * alternative is an edit that appears to work and silently deletes something
 * on save.
 */
export function setParagraphText(model: DocxModel, index: number, text: string): DocxModel {
  const paragraph = model.paragraphs.find((p) => p.index === index);
  if (!paragraph) throw new HandlerError('error.unableToRead', `no paragraph at index ${index}`);
  if (!paragraph.editable) {
    throw new HandlerError(
      'error.featuresUnsupported',
      `paragraph ${index} contains content that text editing cannot preserve`,
    );
  }

  const edits = new Map(model.edits);
  if (text === paragraph.originalText) edits.delete(index);
  else edits.set(index, text);

  // Show the edit immediately, keeping the paragraph's formatting from its
  // first run — the same collapse the save path will perform, so what is on
  // screen is what will be in the file.
  const first = paragraph.runs.find((r) => r.kind === 'text');
  const updated: DocxParagraph = {
    ...paragraph,
    text,
    runs: [{
      kind: 'text',
      text,
      bold: first?.kind === 'text' ? first.bold : false,
      italic: first?.kind === 'text' ? first.italic : false,
      underline: first?.kind === 'text' ? first.underline : false,
      strike: first?.kind === 'text' ? first.strike : false,
      vertical: first?.kind === 'text' ? first.vertical : null,
      highlight: first?.kind === 'text' ? first.highlight : null,
      color: first?.kind === 'text' ? first.color : null,
      href: null,
      revision: null,
    }],
  };

  const replace = (block: DocxBlock): DocxBlock => {
    if (block.kind === 'paragraph') return block.index === index ? updated : block;
    return {
      ...block,
      rows: block.rows.map((row) => row.map((cell) => ({
        ...cell,
        paragraphs: cell.paragraphs.map((p) => (p.index === index ? updated : p)),
      }))),
    };
  };

  return {
    ...model,
    edits,
    blocks: model.blocks.map(replace),
    paragraphs: model.paragraphs.map((p) => (p.index === index ? updated : p)),
    wordCount: countWords(model.paragraphs.map((p) => (p.index === index ? updated : p))),
  };
}

/** Picture dimensions in CSS pixels, for laying an image out at its true size. */
export function imageSizePx(widthEmu: number, heightEmu: number): { width: number; height: number } {
  return {
    width: Math.max(1, Math.round(widthEmu / EMU_PER_PX)),
    height: Math.max(1, Math.round(heightEmu / EMU_PER_PX)),
  };
}

/** Plain text of the whole document, for search and for copying out. */
export function docxToPlainText(model: DocxModel): string {
  const lines: string[] = [];
  const walk = (block: DocxBlock): void => {
    if (block.kind === 'paragraph') { lines.push(block.text); return; }
    for (const row of block.rows) {
      lines.push(row.map((cell) => cell.paragraphs.map((p) => p.text).join(' ')).join('\t'));
    }
  };
  for (const block of model.blocks) walk(block);
  return lines.join('\n');
}
